import React from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import ErrorFallback from './ErrorFallback';
import { VARIANTS } from './ErrorFallback.constants';

const sentryIsLive = () => typeof Sentry.isInitialized === 'function' && Sentry.isInitialized();

const resetKeysChanged = (prevResetKeys, resetKeys) => {
  if (prevResetKeys.length !== resetKeys.length) return true;
  return resetKeys.some((key, index) => key !== prevResetKeys[index]);
};

// Catches render-phase throws in its subtree. Placed at the shell, route and
// panel tiers so one broken component degrades only its own boundary instead
// of unmounting the whole app (c-243). Must be a class: componentDidCatch has
// no hook equivalent in React 16.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, eventId: null };
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const { name } = this.props;
    const eventId = Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
      tags: { errorBoundary: name }
    });
    // Sentry hands back an id even when it was never initialised (dev builds),
    // and that id points at nothing, so it is shown only when an event was sent.
    if (sentryIsLive() && eventId) this.setState({ eventId });
  }

  // Reset only a boundary that was already showing its fallback before the keys
  // changed. A navigation whose new child throws in the same render must stay in
  // the fallback, or it renders the broken child twice and reports twice.
  componentDidUpdate(prevProps, prevState) {
    const { hasError } = this.state;
    const { resetKeys } = this.props;
    if (prevState.hasError && hasError && resetKeysChanged(prevProps.resetKeys, resetKeys)) {
      this.retry();
    }
  }

  retry() {
    this.setState({ hasError: false, error: null, eventId: null });
  }

  render() {
    const { hasError, error, eventId } = this.state;
    const { children, fallback, variant } = this.props;
    if (!hasError) return children;
    if (fallback) return fallback(error, this.retry);
    return <ErrorFallback variant={variant} retry={this.retry} eventId={eventId} />;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  resetKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  fallback: PropTypes.func,
  variant: PropTypes.oneOf(VARIANTS)
};

ErrorBoundary.defaultProps = {
  resetKeys: [],
  fallback: null,
  variant: 'tile'
};

export default ErrorBoundary;
